// ══════════════════════════════════════════════════════════════════════════════
// Fashion Studio, Service Layer
// ══════════════════════════════════════════════════════════════════════════════

import { supabase } from '../lib/supabaseClient';
import { getOrCreateAnonId } from './auraIdentity';

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL  as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const TEMP_BUCKET       = 'tryon-temp';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface InventoryItem {
  id:               string;
  listing_id:       string;
  title:            string;
  designer_name:    string | null;
  category:         'bridal_gown' | 'menswear' | 'bridesmaid' | 'accessory';
  silhouette_tag:   string | null;
  fabric_tag:       string | null;
  style_tags:       string[];
  price_numeric:    number | null;
  price_currency:   string;
  price_tier:       string | null;
  available_sizes:  string[];
  product_url:          string | null;
  listing_tryon_assets: { raw_product_url: string }[];
  listing: {
    name:            string;
    city:            string;
    country:         string;
    contact_profile: { website?: string | null } | null;
    slug?:           string | null;
    country_slug?:   string | null;
    region_slug?:    string | null;
    category_slug?:  string | null;
  } | null;
}

export interface TryOnSession {
  session_id:       string;
  fashn_image_id:   string;
  image_status:     'in_queue' | 'processing' | 'completed' | 'failed';
  result_image_url: string | null; // null until status endpoint marks completed
  video_status:     'pending' | 'generating' | 'ready' | 'failed';
  result_video_url: string | null;
  garment: {
    title:          string;
    designer_name:  string | null;
    silhouette_tag: string | null;
    fabric_tag:     string | null;
  };
}

export interface SavedLook {
  id:              string;
  items:           string[];
  result_image_url: string | null;
  result_video_url: string | null;
  palette:         string | null;
  silhouette_tag:  string | null;
  fabric_tag:      string | null;
  created_at:      string;
}

// ── Portrait upload ───────────────────────────────────────────────────────────
// Uploads the bride's photo to ephemeral storage so IDM-VTON can access it.
// Files in tryon-temp are auto-deleted after 24h via storage lifecycle policy.

export async function uploadPortraitTemp(file: File): Promise<string> {
  const ext      = file.name.split('.').pop() ?? 'jpg';
  const filename = `portraits/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { error } = await supabase.storage
    .from(TEMP_BUCKET)
    .upload(filename, file, { cacheControl: '86400', upsert: false });

  if (error) throw new Error(`Portrait upload failed: ${error.message}`);

  const { data: { publicUrl } } = supabase.storage
    .from(TEMP_BUCKET)
    .getPublicUrl(filename);

  return publicUrl;
}

// ── Boutique inventory ────────────────────────────────────────────────────────

export interface InventoryFilters {
  category?: string;
  silhouette?: string;
  country?: string;
  priceTier?: string;
  listingId?: string;
  limit?: number;
}

export async function fetchBoutiqueInventory(
  filters: InventoryFilters = {}
): Promise<InventoryItem[]> {
  let query = supabase
    .from('listing_inventory')
    .select(`
      id, listing_id, title, designer_name, category,
      silhouette_tag, fabric_tag, style_tags,
      price_numeric, price_currency, price_tier, available_sizes,
      product_url,
      listing_tryon_assets (raw_product_url),
      listing:listings (name, city, country, contact_profile, slug, country_slug, region_slug, category_slug)
    `)
    .eq('is_published', true)
    .order('sort_order', { ascending: true })
    .limit(filters.limit ?? 48);

  if (filters.category)   query = query.eq('category',      filters.category);
  if (filters.silhouette) query = query.eq('silhouette_tag', filters.silhouette);
  if (filters.priceTier)  query = query.eq('price_tier',    filters.priceTier);
  if (filters.listingId)  query = query.eq('listing_id',    filters.listingId);

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as InventoryItem[];
}

// Fetch all published inventory items for a single listing (boutique profile page).
export async function fetchInventoryByListing(listingId: string): Promise<InventoryItem[]> {
  const { data, error } = await supabase
    .from('listing_inventory')
    .select(`
      id, listing_id, title, designer_name, category,
      silhouette_tag, fabric_tag, style_tags,
      price_numeric, price_currency, price_tier, available_sizes,
      product_url,
      listing_tryon_assets (raw_product_url)
    `)
    .eq('listing_id', listingId)
    .eq('is_published', true)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data ?? []) as InventoryItem[];
}

// ── Try-on call ───────────────────────────────────────────────────────────────

export interface TryOnParams {
  inventory_id:     string;
  person_image_url: string;
  session_id:       string;
  anonymous_id?:    string | null;
  palette_selected?: string;
}

export async function callTryOn(params: TryOnParams): Promise<TryOnSession> {
  const res = await fetch(
    `${SUPABASE_URL}/functions/v1/fashion-tryon`,
    {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey':        SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(params),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error ?? `Try-on failed (${res.status})`);
  }

  return res.json();
}

// ── Image status polling (async Fashn pipeline) ───────────────────────────────
// Client calls this every 3s while screen === 'generating'.
// When image_status === 'completed', result_image_url is populated.

export async function pollTryOnImageStatus(tryonSessionId: string): Promise<{
  image_status:     string;
  result_image_url: string | null;
  video_status:     string;
}> {
  const res = await fetch(
    `${SUPABASE_URL}/functions/v1/fashion-tryon-status`,
    {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey':        SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ session_id: tryonSessionId }),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error ?? `Status check failed (${res.status})`);
  }

  return res.json();
}

// ── Video status polling ──────────────────────────────────────────────────────
// Calls the edge function rather than reading the DB directly. Each call
// checks Fashn video status and updates tryon_sessions if the video is ready.

export async function pollVideoStatus(
  tryonSessionId: string
): Promise<{ video_status: string; result_video_url: string | null; video_expires_at: string | null }> {
  const res = await fetch(
    `${SUPABASE_URL}/functions/v1/fashion-tryon-status`,
    {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey':        SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ session_id: tryonSessionId }),
    }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error ?? `Video status check failed (${res.status})`);
  }

  const data = await res.json();
  return {
    video_status:     data.video_status     ?? 'generating',
    result_video_url: data.result_video_url ?? null,
    video_expires_at: data.video_expires_at ?? null,
  };
}

// ── Request a film (opt-in) ───────────────────────────────────────────────────
// Films are NOT auto-generated (credit-saving). This explicitly asks the edge
// function to submit the image-to-video prediction (deducts one video credit).
export async function requestTryOnVideo(
  tryonSessionId: string
): Promise<{ video_status: string }> {
  const res = await fetch(
    `${SUPABASE_URL}/functions/v1/fashion-tryon-status`,
    {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey':        SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ session_id: tryonSessionId, request_video: true }),
    }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error ?? `Film request failed (${res.status})`);
  }
  const data = await res.json();
  return { video_status: data.video_status ?? 'generating' };
}

// ── Save look ─────────────────────────────────────────────────────────────────

export const LOOK_COLUMNS =
  'id, items, result_image_url, result_video_url, palette, silhouette_tag, fabric_tag, created_at';

// Returns the saved row (insert chains .select(), so an RLS-blocked read-back
// surfaces here as an error instead of a silent void). Verified-save contract:
// no silent saves, no assumed saves.
export async function saveLook(look: {
  anonymous_id:     string;
  couple_id?:       string | null;
  listing_id:       string;
  inventory_ids:    string[];
  result_image_url: string;
  result_video_url: string | null;
  palette:          string | null;
  silhouette_tag:   string | null;
  fabric_tag:       string | null;
}) {
  const { data, error } = await supabase
    .from('bridal_looks')
    .insert({
      anonymous_id:     look.anonymous_id,
      couple_id:        look.couple_id ?? null,
      listing_id:       look.listing_id,
      items:            look.inventory_ids,
      result_image_url: look.result_image_url,
      result_video_url: look.result_video_url ?? null,
      palette:          look.palette ?? null,
      silhouette_tag:   look.silhouette_tag ?? null,
      fabric_tag:       look.fabric_tag ?? null,
    })
    .select(LOOK_COLUMNS)
    .single();

  if (error) throw error;
  return data;
}

// Fresh read of a saved look through RLS with the active session, the
// read-back half of verifiedSave. Returns null when the row is not visible.
// Time-boxed so a stalled read-back fails fast instead of hanging the save.
export async function fetchLookById(id: string) {
  const { data, error } = await supabase
    .from('bridal_looks')
    .select(LOOK_COLUMNS)
    .eq('id', id)
    .abortSignal(AbortSignal.timeout(10_000))
    .maybeSingle();

  if (error) throw error;
  return data;
}

// ── Fetch Aura fashion profile ────────────────────────────────────────────────

export async function fetchFashionProfile(anonymousId: string) {
  const { data } = await supabase
    .from('aura_user_profiles')
    .select(`
      detected_dress_silhouette,
      detected_dress_fabric,
      detected_dress_style,
      fashion_palette_selected,
      fashion_boutique_country,
      fashion_tryon_count
    `)
    .eq('anonymous_id', anonymousId)
    .single();

  return data;
}

// ── Format price ──────────────────────────────────────────────────────────────

export function formatPrice(item: InventoryItem): string {
  if (!item.price_numeric) return '';
  const symbol = item.price_currency === 'USD' ? '$'
               : item.price_currency === 'EUR' ? '€' : '£';
  return `${symbol}${item.price_numeric.toLocaleString()}`;
}

// ── Price tier label ──────────────────────────────────────────────────────────

export function priceTierLabel(tier: string | null): string {
  switch (tier) {
    case 'budget':      return '£';
    case 'mid':         return '££';
    case 'luxury':      return '£££';
    case 'ultra_luxury': return '££££';
    default:            return '';
  }
}

// ── Affiliate click tracking ──────────────────────────────────────────────────
// Fire-and-forget. Never blocks the outbound navigation.

export interface StudioClickParams {
  inventory_id:    string | null;
  listing_id:      string | null;
  anonymous_id:    string | null;
  couple_id?:      string | null;
  destination_url: string;
  click_type:      'shop_dress' | 'boutique_website' | 'view_boutique' | 'book_appointment';
  palette?:        string | null;
  silhouette_tag?: string | null;
}

export async function logStudioClick(params: StudioClickParams): Promise<void> {
  try {
    await supabase.from('studio_click_events').insert({
      inventory_id:    params.inventory_id,
      listing_id:      params.listing_id,
      anonymous_id:    params.anonymous_id,
      couple_id:       params.couple_id ?? null,
      destination_url: params.destination_url,
      click_type:      params.click_type,
      palette:         params.palette ?? null,
      silhouette_tag:  params.silhouette_tag ?? null,
    });
  } catch {
    // Never surface errors for analytics calls
  }
}

// ── Studio Action Tracking ────────────────────────────────────────────────────
// Track CTA clicks in post-reveal conversion layer

export interface StudioActionParams {
  action_id:       string;
  session_id:      string;
  gown_id:         string | null;
  anonymous_id:    string | null;
  couple_id?:      string | null;
  destination_url: string;
}

export async function logStudioAction(params: StudioActionParams): Promise<void> {
  try {
    await supabase.from('studio_click_events').insert({
      inventory_id:    params.gown_id,
      anonymous_id:    params.anonymous_id,
      couple_id:       params.couple_id ?? null,
      destination_url: params.destination_url,
      click_type:      params.action_id,
    });
  } catch {
    // Never surface errors for analytics calls
  }
}

// ── Fashion try-on lead ───────────────────────────────────────────────────────
// Captures a bridal lead at the moment of virtual try-on registration.

export interface FashionTryOnLead {
  email:               string;
  first_name:          string;
  boutique_listing_id?: string | null;
  boutique_name?:      string | null;
  couple_id?:          string | null;
  selected_gown_id?:   string | null;
  selected_gown_name?: string | null;
  portrait_uploaded?:  boolean;
  wedding_date?:       string | null;
  location?:           string | null;
  dress_budget?:       string | null;
  appointment_type?:   string | null;
  message?:            string | null;
  newsletter_opt_in?:  boolean;
  source?:             string;
  session_id?:         string | null;
}

export async function saveFashionTryOnLead(lead: FashionTryOnLead): Promise<void> {
  const { error } = await supabase.from('fashion_tryon_leads').insert({
    email:               lead.email.trim().toLowerCase(),
    first_name:          lead.first_name.trim(),
    boutique_listing_id: lead.boutique_listing_id ?? null,
    boutique_name:       lead.boutique_name ?? null,
    couple_id:           lead.couple_id ?? null,
    selected_gown_id:    lead.selected_gown_id ?? null,
    selected_gown_name:  lead.selected_gown_name ?? null,
    portrait_uploaded:   lead.portrait_uploaded ?? false,
    wedding_date:        lead.wedding_date ?? null,
    location:            lead.location ?? null,
    dress_budget:        lead.dress_budget ?? null,
    appointment_type:    lead.appointment_type ?? null,
    message:             lead.message ?? null,
    newsletter_opt_in:   lead.newsletter_opt_in ?? false,
    source:              lead.source ?? 'fashion_studio',
    session_id:          lead.session_id ?? null,
  });
  if (error) throw error;
}

// ── Appointment notification (default contact routing) ────────────────────────
// Routes a Fashion Studio appointment request to the boutique's listing contact
// email (the stopgap default) and confirms the bride. Non-blocking.
export async function notifyFashionAppointment(p: {
  listingId:    string | null;
  boutiqueName: string | null;
  brideName:    string;
  brideEmail:   string;
  weddingDate?: string | null;
  gownName?:    string | null;
  message?:     string | null;
}): Promise<void> {
  try {
    const noEm  = (s: string) => s.replace(/\s*—\s*/g, ' - ');
    const house = p.boutiqueName || 'the boutique';
    const gown  = p.gownName ? noEm(p.gownName) : null;

    let boutiqueEmail: string | null = null;
    if (p.listingId) {
      const { data } = await supabase.from('listings').select('contact_profile').eq('id', p.listingId).maybeSingle();
      boutiqueEmail = ((data?.contact_profile as Record<string, unknown> | null)?.email as string) || null;
    }

    const detail = [
      gown          ? `Gown: ${gown}` : null,
      p.weddingDate ? `Wedding date: ${p.weddingDate}` : null,
      `Email: ${p.brideEmail}`,
      p.message     ? `Note: ${p.message}` : null,
    ].filter(Boolean).join('<br>');

    // 1. The boutique gets the lead (default routing = listing contact email).
    if (boutiqueEmail) {
      await supabase.functions.invoke('send-email', { body: {
        to: boutiqueEmail,
        subject: `New private fitting request from ${p.brideName}`,
        html: `<div style="font-family:Helvetica,Arial,sans-serif;max-width:560px;margin:0 auto;color:#1a1714;"><div style="background:#1a1714;padding:26px 36px;text-align:center;"><span style="font-family:Georgia,serif;font-size:12px;color:#d39a21;letter-spacing:3px;">L W D &nbsp; FASHION STUDIO</span></div><div style="padding:30px 36px;"><p style="font-family:Georgia,serif;font-size:20px;margin:0 0 16px;">A bride has requested a fitting.</p><p style="font-size:15px;line-height:1.7;color:#4a4540;margin:0 0 14px;"><strong>${p.brideName}</strong> tried on your gowns in the Fashion Studio and asked to book a private fitting at ${house}.</p><div style="background:#f9f7f2;border-left:3px solid #d39a21;padding:14px 18px;margin:18px 0;font-size:14px;color:#4a4540;line-height:1.9;">${detail}</div><p style="font-size:14px;color:#4a4540;">Reply to ${p.brideEmail} to arrange her appointment.</p></div></div>`,
      }}).catch(() => {});
    }

    // 2. The bride gets a confirmation.
    if (p.brideEmail) {
      await supabase.functions.invoke('send-email', { body: {
        to: p.brideEmail,
        subject: `Your fitting request with ${house}`,
        html: `<div style="font-family:Helvetica,Arial,sans-serif;max-width:560px;margin:0 auto;color:#1a1714;"><div style="background:#1a1714;padding:26px 36px;text-align:center;"><span style="font-family:Georgia,serif;font-size:12px;color:#d39a21;letter-spacing:3px;">L W D</span></div><div style="padding:30px 36px;"><p style="font-family:Georgia,serif;font-size:22px;font-style:italic;margin:0 0 16px;">Dear ${p.brideName},</p><p style="font-size:15px;line-height:1.7;color:#4a4540;">Your request for a private fitting at <strong>${house}</strong> has been received. They will be in touch to arrange it.${gown ? ` We have let them know you loved the ${gown}.` : ''}</p></div><div style="border-top:1px solid #f0ede6;padding:22px 36px;text-align:center;"><p style="font-size:12px;color:#9ca3af;margin:0;">Luxury Wedding Directory</p></div></div>`,
      }}).catch(() => {});
    }
  } catch { /* non-blocking */ }
}

// ── Bride identity enrichment ─────────────────────────────────────────────────
// Route the atelier form's DECLARED answers into the identity layer (not just the
// lead). Declared destination/budget land on the couple when she is logged in;
// budget/date also ride the anonymous Aura profile so they survive the login
// claim. Returns the resolved couple_id (null if anonymous) so the caller can
// tag the lead. Non-blocking, failures never break the enquiry.
export async function enrichBrideIdentity(opts: {
  destination?: string | null;
  budget?:      string | null;
  weddingDate?: string | null;
}): Promise<string | null> {
  try {
    const anonId = getOrCreateAnonId();
    let coupleId: string | null = null;
    const { data: auth } = await supabase.auth.getUser();
    if (auth?.user) {
      const { data: couple } = await supabase
        .from('couples').select('id').eq('user_id', auth.user.id).maybeSingle();
      coupleId = couple?.id ?? null;
    }
    await supabase.rpc('enrich_bride_identity', {
      p_couple_id:         coupleId,
      p_anon_id:           anonId,
      p_destination:       opts.destination || null,
      p_budget_band:       opts.budget || null,
      p_wedding_date_hint: opts.weddingDate || null,
      p_bridal_style:      null,
    });
    return coupleId;
  } catch {
    return null;
  }
}

// ── Portrait moderation ───────────────────────────────────────────────────────
// Calls the moderate-portrait edge function to check for inappropriate content.
// Fails open, infrastructure errors never block a legitimate portrait upload.

export async function moderatePortrait(imageUrl: string): Promise<{ safe: boolean; limited?: boolean }> {
  // 8-second hard timeout, OpenAI vision can be slow; we always fail open
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8_000);
  // Session id so rate limiting is per-session (shared-IP safe), matching the
  // telemetry pipeline key.
  let sid = '';
  try { sid = sessionStorage.getItem('lwd_sid') || ''; } catch { /* no storage */ }
  try {
    const res = await fetch(
      `${SUPABASE_URL}/functions/v1/moderate-portrait`,
      {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey':        SUPABASE_ANON_KEY,
        },
        body:   JSON.stringify({ imageUrl, session_id: sid }),
        signal: controller.signal,
      }
    );
    if (!res.ok) return { safe: true };
    const data = await res.json();
    // Rate-limited: NOT safe (no bypass) and NOT inappropriate, a soft "please wait".
    if (data.limited) return { safe: false, limited: true };
    return { safe: data.safe !== false };
  } catch {
    // Timeout, network error, or any other failure, always fail open
    return { safe: true };
  } finally {
    clearTimeout(timer);
  }
}

// ── Try-On Settings ─────────────────────────────────────────────────────────────
// Per-listing configuration for tracking, access control, and data retention

export interface TryOnSettings {
  id: string;
  listing_id: string;
  enable_tryon: boolean;
  require_user_account: boolean;
  require_email_capture: boolean;
  require_consent_before_upload: boolean;
  track_garment_clicks: boolean;
  track_upload_started: boolean;
  track_generation_started: boolean;
  track_generation_completed: boolean;
  track_save_to_identity: boolean;
  send_lead_notification: boolean;
  notification_email: string | null;
  retention_days: number;
  booking_url: string | null;
  gallery_url: string | null;
  shop_url: string | null;
  whatsapp_url: string | null;
  consultation_url: string | null;
  show_booking_action: boolean;
  show_gallery_action: boolean;
  show_shop_action: boolean;
  show_whatsapp_action: boolean;
  show_consultation_action: boolean;
  created_at: string;
  updated_at: string;
}

export async function fetchTryOnSettings(listingId: string): Promise<TryOnSettings | null> {
  const { data, error } = await supabase
    .from('listing_tryon_settings')
    .select('*')
    .eq('listing_id', listingId)
    .single();

  if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
  return (data as TryOnSettings) || null;
}

export async function saveTryOnSettings(listingId: string, settings: Partial<Omit<TryOnSettings, 'id' | 'listing_id' | 'created_at' | 'updated_at'>>): Promise<TryOnSettings> {
  const { data, error } = await supabase
    .from('listing_tryon_settings')
    .upsert({
      listing_id: listingId,
      ...settings,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'listing_id' })
    .select()
    .single();

  if (error) throw error;
  return data as TryOnSettings;
}
